// The things that make it a fax rather than a black-and-white photo.
//
// Everything here is driven by ONE seeded generator, so a fax re-renders
// byte-identically forever. That is not a nicety — it is what lets the outbox
// store a recipe (source, seed, condition) instead of a bitmap, which is the
// difference between an outbox that costs kilobytes and one that costs
// megabytes.
//
// Everything scales off a single 0..1 `wreck` value, which the CONDITION
// control sets. At wreck 0 (MINT) every function here is a no-op by
// construction: MINT IS A LEGIBILITY FLOOR. It is what a low-vision user
// switches to in order to read the thing at all, and it must never be merely
// "a bit less damaged".
//
// Two groups, and the order matters:
//   PRE-THRESHOLD  changes to tone. These become density once dithered, so they
//                  read as ink coverage rather than as a grey wash.
//   POST-THRESHOLD changes to pixels. These MUST write pure 0 or 255, because
//                  the exported image's invariant is that no grey exists.

import { PURE_BLACK, PURE_WHITE } from './geometry.js';

/** mulberry32 — small, fast, and good enough that streaks do not visibly repeat. */
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * PRE: toner banding.
 *
 * A low-frequency sine over the page, tiny amplitude. Real machines lay ink
 * unevenly as the drum turns, and it shows as soft horizontal bands of slightly
 * heavier coverage. Applied to tone, so the dither turns it into density.
 */
export function tonerBanding(grey, w, h, wreck, rnd) {
  if (wreck <= 0) return grey;
  const amp = 10 * wreck;
  const phase = rnd() * Math.PI * 2;
  const freq = (2 + rnd() * 3) / h;
  for (let y = 0; y < h; y += 1) {
    const d = Math.sin(y * freq * Math.PI * 2 + phase) * amp;
    if (!d) continue;
    const row = y * w;
    for (let x = 0; x < w; x += 1) {
      const v = grey[row + x] + d;
      grey[row + x] = v < 0 ? 0 : v > 255 ? 255 : v;
    }
  }
  return grey;
}

/**
 * POST: vertical streaks.
 *
 * A dirty roller drags a mark down the whole page. This is the single most
 * recognisable photocopier artefact there is, it is six lines of code, and it
 * is the highest joke-per-byte in the app.
 */
export function verticalStreaks(bits, w, h, wreck, rnd) {
  if (wreck <= 0) return bits;
  const count = Math.round(1 + rnd() * 4 * wreck);
  for (let n = 0; n < count; n += 1) {
    const x = Math.floor(rnd() * w);
    const white = rnd() > 0.25; // mostly a wiped white line, occasionally an inked one
    const v = white ? PURE_WHITE : PURE_BLACK;
    for (let y = 0; y < h; y += 1) bits[y * w + x] = v;
  }
  return bits;
}

/**
 * POST: line slip.
 *
 * The receiving machine loses sync and every row below the fault shifts
 * sideways. On a real fax this is jarring and permanent for the rest of the
 * page; here it is bounded so the page stays readable.
 */
export function lineSlip(bits, w, h, wreck, rnd) {
  if (wreck <= 0) return bits;
  const faults = Math.round(rnd() * 3 * wreck);
  for (let n = 0; n < faults; n += 1) {
    const at = Math.floor(rnd() * h);
    const shift = Math.round((rnd() * 2 - 1) * 12 * wreck);
    if (!shift) continue;
    for (let y = at; y < Math.min(h, at + 6 + rnd() * 40); y += 1) {
      const row = y * w;
      const copy = bits.slice(row, row + w);
      for (let x = 0; x < w; x += 1) {
        const src = x - shift;
        bits[row + x] = src >= 0 && src < w ? copy[src] : PURE_WHITE;
      }
    }
  }
  return bits;
}

/** POST: dropout rows — data lost (white) or line noise (black). */
export function dropoutRows(bits, w, h, wreck, rnd) {
  if (wreck <= 0) return bits;
  const rows = Math.round(rnd() * 4 * wreck);
  for (let n = 0; n < rows; n += 1) {
    const y = Math.floor(rnd() * h);
    const v = rnd() > 0.3 ? PURE_WHITE : PURE_BLACK;
    bits.fill(v, y * w, y * w + w);
  }
  return bits;
}

/** POST: edge crud — the scanner picking up dirt at the page margins. */
export function edgeCrud(bits, w, h, wreck, rnd) {
  if (wreck <= 0) return bits;
  const depth = Math.round(3 + rnd() * 5 * wreck);
  for (let y = 0; y < h; y += 1) {
    for (let e = 0; e < depth; e += 1) {
      if (rnd() < 0.35 * wreck) bits[y * w + e] = PURE_BLACK;
      if (rnd() < 0.35 * wreck) bits[y * w + (w - 1 - e)] = PURE_BLACK;
    }
  }
  return bits;
}

/**
 * POST: the bottom curl.
 *
 * The last rows degrade as the thermal roll runs out. Bounded to the very
 * bottom so it never eats content.
 */
export function bottomCurl(bits, w, h, wreck, rnd) {
  if (wreck <= 0) return bits;
  const band = Math.round(18 * wreck);
  for (let n = 0; n < band; n += 1) {
    const y = h - 1 - n;
    if (y < 0) break;
    const noise = (n / band) * wreck;
    const row = y * w;
    for (let x = 0; x < w; x += 1) {
      if (rnd() < noise * 0.5) bits[row + x] = rnd() > 0.5 ? PURE_WHITE : PURE_BLACK;
    }
  }
  return bits;
}

/** Everything post-threshold, in the order a real machine would inflict it. */
export function applyPost(bits, w, h, wreck, rnd) {
  lineSlip(bits, w, h, wreck, rnd);
  dropoutRows(bits, w, h, wreck, rnd);
  verticalStreaks(bits, w, h, wreck, rnd);
  edgeCrud(bits, w, h, wreck, rnd);
  bottomCurl(bits, w, h, wreck, rnd);
  return bits;
}
